// The editor header's Link list: which machines, and what each row offers.
import type { Branch, Unit } from '../lib/types'
import type { LayoutKind } from './types'

/** The design kinds a machine shows on a screen; a ticket is paper. */
export function linksOffered(kind: LayoutKind): boolean {
  return kind === 'display' || kind === 'counter' || kind === 'kiosk'
}

export interface LinkRow {
  unit: Unit
  branchCode: string
  /** "Baş ofis · TV1" */
  label: string
}

/** The machines as rows, by branch then machine name; a machine whose branch is unknown is left out. */
export function linkRows(machines: readonly Unit[], branches: readonly Branch[]): LinkRow[] {
  const byId = new Map(branches.map((b) => [b.id, b]))
  const rows: LinkRow[] = []
  for (const unit of machines) {
    const b = byId.get(unit.branchId)
    if (b) rows.push({ unit, branchCode: b.code, label: `${b.name} · ${unit.name}` })
  }
  const by = (a: string, b: string) => a.localeCompare(b, undefined, { numeric: true, sensitivity: 'base' })
  return rows.sort((a, b) => by(a.label, b.label))
}

export interface KeyState {
  hasKey: boolean
  required: boolean
}

/** What a row offers: the plain address unless the server would refuse it. */
export function linkOffer(key: KeyState | undefined): { copy: boolean } {
  if (!key) return { copy: false }
  return { copy: key.hasKey || !key.required }
}

interface Clip {
  write?: (items: ClipboardItem[]) => Promise<void>
  writeText?: (text: string) => Promise<void>
}

/**
 * Copies a text that is still on its way. Call it before any await in the click:
 * write() with a ClipboardItem promise keeps the click's permission while the
 * request runs; writeText after the await is the fallback. False when neither took it.
 */
export async function copyWhenReady(
  text: Promise<string>,
  clip: Clip | undefined = globalThis.navigator?.clipboard,
  Item: typeof ClipboardItem | undefined = globalThis.ClipboardItem,
): Promise<boolean> {
  if (clip?.write && Item) {
    const blob = text.then((t) => new Blob([t], { type: 'text/plain' }))
    blob.catch(() => {}) // a failed request is the caller's to report
    try {
      await clip.write([new Item({ 'text/plain': blob })])
      return true
    } catch {
      // Falls through to writeText.
    }
  }
  try {
    if (!clip?.writeText) return false
    await clip.writeText(await text)
    return true
  } catch {
    return false
  }
}
